import React, { createContext, useState, useContext } from 'react';
import apiService from '../services/apiService';

// Function to transform plan data (e.g., renaming _id to id)
const transformWorkPlans = (plans) => {
  if (!Array.isArray(plans)) return [];
  return plans.map((plan) => {
    const { _id, ...rest } = plan;
    return { id: _id, ...rest };
  });
};

const transformWorkPlan = (plan) => {
  const { _id, ...rest } = plan;
  return { id: _id, ...rest };
};

// Create the context
const WorkPlanContext = createContext();

// Create a custom hook for easier access to the context
export const usePlan = () => {
  return useContext(WorkPlanContext);
};

// Export the provider to wrap around the app
export const WorkPlanProvider = ({ children }) => {
  const [plans, setPlans] = useState([]);
  const [currentQuery, setCurrentQuery] = useState({});
  const [plansByTeam, setPlansByTeam] = useState([]);
  const [plansByProject, setPlansByProject] = useState([]);

  // Function to add a new plan
  const addPlan = async (plan) => {
    const { content, end_day, start_day, project, work_time_per_day } = plan;
    try {
      await apiService.post('/api/v1/work-plan', {
        project: project,
        content,
        end_day,
        start_day,
        work_time_per_day,
      });
      await fetchPlans(currentQuery);
    } catch (error) {
      console.error('Error adding plan:', error);
    }
  };

  // Function to remove a plan by id
  const removePlan = async (id) => {
    try {
      await apiService.delete(`/api/v1/work-plan/${id}`);
      setPlans((prevPlans) => prevPlans.filter((plan) => plan.id !== id));
    } catch (error) {
      console.error('Error removing plan:', error);
      // Optionally, handle errors
    }
  };

  // Function to update a plan
  const updatePlan = async (updatedPlan) => {
    try {
      const { data } = await apiService.patch(
        `/api/v1/work-plan/${updatedPlan.id}`,
        updatedPlan
      );
      // Assuming the response data is in `data`
      setPlans((prevPlans) =>
        prevPlans.map((plan) =>
          plan.id === data._id ? transformWorkPlan(data) : plan
        )
      );
    } catch (error) {
      console.error('Error updating plan:', error);
      // Optionally, handle errors
    }
  };

  // Function to fetch all plans
  const fetchPlans = async (params) => {
    try {
      const { data } = await apiService.get('/api/v1/work-plan', { params });
      setCurrentQuery(params);
      setPlans(transformWorkPlans(data || []));
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Optionally, handle errors
    }
  };

  // Function to fetch plans by project
  const fetchPlansByProject = async (params) => {
    try {
      const { data } = await apiService.get(
        '/api/v1/work-plan/get-by-project',
        { params }
      );
      setPlansByProject(data || []);
    } catch (error) {
      console.error('Error fetching plans by project:', error);
      // Optionally, handle errors
    }
  };

  const fetchPlansByTeam = async (params) => {
    try {
      const { data } = await apiService.get('/api/v1/work-plan/get-by-team', {
        params,
      });
      setPlansByTeam(data || []);
    } catch (error) {
      console.error('Error fetching plans by project:', error);
      // Optionally, handle errors
    }
  };

  // Function to get a plan by id
  const getPlanById = (id) => {
    return plans.find((plan) => plan.id === id) || null;
  };

  return (
    <WorkPlanContext.Provider
      value={{
        plans,
        plansByTeam,
        plansByProject,
        addPlan,
        removePlan,
        updatePlan,
        fetchPlans,
        getPlanById,
        fetchPlansByProject,
        fetchPlansByTeam,
      }}>
      {children}
    </WorkPlanContext.Provider>
  );
};
